"use client";

import { useEffect, useRef } from "react";
import { TrendingUp, Users2Icon, Clock, DollarSign } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import backgroundImage from "../../assets/LandingPage/background-metrics.png";
import SourceIQLogo from "../UILibrary/SourceIQLogo";

export default function ImpactMetrics() {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const section = sectionRef.current;
    const cards = cardsRef.current;
    if (!section || cards.length === 0) return;

    const navbarHeight = 64;
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: `top-=${navbarHeight} top`,
        end: `+=${cards.length * 700}`,
        scrub: 2,
        pin: true,
        anticipatePin: 1,
        pinSpacing: true,
      },
    });

    cards.forEach((card, i) => {
      tl.fromTo(
        card,
        { autoAlpha: 0, y: 80, scale: 0.95 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 1.2, ease: "power2.out" },
        i
      );

      if (i > 0) {
        tl.to(
          cards[i - 1],
          {
            autoAlpha: 0,
            y: -80,
            scale: 0.95,
            duration: 1.2,
            ease: "power2.in",
          },
          i
        );
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      tl.kill();
    };
  }, []);

  const metricsData = [
    {
      percentage: "87%",
      title: "Faster Sourcing",
      description: "Reduce sourcing time with AI-powered matching",
      icon: TrendingUp,
    },
    {
      percentage: "$2.3M",
      title: "Average Savings",
      description: "Cost reduction through optimized sourcing",
      icon: DollarSign,
    },
    {
      percentage: "100K+",
      title: "Verified Suppliers",
      description: "Pre-screened supplier database",
      icon: Users2Icon,
    },
    {
      percentage: "24hrs",
      title: "Setup Time",
      description: "Cloud-native deployment speed",
      icon: Clock,
    },
  ];

  const addToRefs = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  return (
    <section ref={sectionRef} className="relative min-h-[750px]">
      <div className="absolute top-6 right-6 sm:top-16 sm:right-24 z-20">
        <SourceIQLogo fill="rgba(255, 255, 255, 0.5)" width={230} height={46} />
      </div>

      <div
        className="flex flex-col-reverse sm:flex-row gap-2 md:gap-56 justify-[start] md:justify-center items-center lg:items-start px-6 sm:px-8 md:px-24 lg:px-12 py-16 sm:py-24 lg:py-32 relative h-full"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          justifyContent: "start",
        }}
      >
        <div className="w-full lg:w-1/3 relative flex justify-center lg:justify-start lg:mt-0">
          <div className="relative w-full max-w-sm mt-24">
            {metricsData.map((metric, i) => {
              const IconComponent = metric.icon;
              return (
                <div
                  key={i}
                  ref={addToRefs}
                  className="card absolute top-0 w-full bg-white rounded-2xl shadow-2xl p-6 sm:p-8 h-[200px] sm:h-[280px]"
                  style={{
                    willChange: "transform, opacity",
                    transform: "translateZ(0)",
                  }}
                >
                  <div className="flex items-center justify-center mb-4 sm:mb-6">
                    <IconComponent className="w-10 h-10 sm:w-12 sm:h-12 text-blue-500" />
                  </div>
                  <div className="text-center">
                    <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-2">
                      {metric.percentage}
                    </div>
                    <div className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
                      {metric.title}
                    </div>
                    <p className="text-gray-600 text-sm sm:text-base">
                      {metric.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-24 w-full lg:w-1/3 lg:ml-12 text-center lg:text-left mb-0 lg:mb-0">
          <div className="bg-[#EAF524] rounded-full px-6 py-2 w-fit mb-8 flex flex-row justify-center items-center mx-auto">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className="mr-2"
            >
              <path
                d="M3 21C2.71667 21 2.47917 20.9042 2.2875 20.7125C2.09583 20.5208 2 20.2833 2 20C2 19.7167 2.09583 19.4792 2.2875 19.2875C2.47917 19.0958 2.71667 19 3 19H21C21.2833 19 21.5208 19.0958 21.7125 19.2875C21.9042 19.4792 22 19.7167 22 20C22 20.2833 21.9042 20.5208 21.7125 20.7125C21.5208 20.9042 21.2833 21 21 21H3ZM4.5 18C4.08333 18 3.72917 17.8542 3.4375 17.5625C3.14583 17.2708 3 16.9167 3 16.5V12.5C3 12.0833 3.14583 11.7292 3.4375 11.4375C3.72917 11.1458 4.08333 11 4.5 11C4.91667 11 5.27083 11.1458 5.5625 11.4375C5.85417 11.7292 6 12.0833 6 12.5V16.5C6 16.9167 5.85417 17.2708 5.5625 17.5625C5.27083 17.8542 4.91667 18 4.5 18ZM9.5 18C9.08333 18 8.72917 17.8542 8.4375 17.5625C8.14583 17.2708 8 16.9167 8 16.5V7.5C8 7.08333 8.14583 6.72917 8.4375 6.4375C8.72917 6.14583 9.08333 6 9.5 6C9.91667 6 10.2708 6.14583 10.5625 6.4375C10.8542 6.72917 11 7.08333 11 7.5V16.5C11 16.9167 10.8542 17.2708 10.5625 17.5625C10.2708 17.8542 9.91667 18 9.5 18ZM14.5 18C14.0833 18 13.7292 17.8542 13.4375 17.5625C13.1458 17.2708 13 16.9167 13 16.5V10.5C13 10.0833 13.1458 9.72917 13.4375 9.4375C13.7292 9.14583 14.0833 9 14.5 9C14.9167 9 15.2708 9.14583 15.5625 9.4375C15.8542 9.7292 16 10.0833 16 10.5V16.5C16 16.9167 15.8542 17.2708 15.5625 17.5625C15.2708 17.8542 14.9167 18 14.5 18ZM19.5 18C19.0833 18 18.7292 17.8542 18.4375 17.5625C18.1458 17.2708 18 16.9167 18 16.5V4.5C18 4.08333 18.1458 3.72917 18.4375 3.4375C18.7292 3.14583 19.0833 3 19.5 3C19.9167 3 20.2708 3.14583 20.5625 3.4375C20.8542 3.72917 21 4.08333 21 4.5V16.5C21 16.9167 20.8542 17.2708 20.5625 17.5625C20.2708 17.8542 19.9167 18 19.5 18Z"
                fill="#1D2739"
              />
            </svg>
            <span className="text-base font-semibold text-[#1D2739]">
              Impact Metrics
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Proven Results That
            <br />
            Drive Growth
          </h2>
          <p className="text-white text-base sm:text-lg leading-relaxed max-w-md mx-auto lg:mx-0">
            Join hundreds of organizations that have transformed their
            procurement process with SourceIQ.
          </p>
        </div>
      </div>
    </section>
  );
}
